import { useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { useAuth, AuthContextValue } from "../../providers/AuthProvider";
import { useGetOutputPrice } from "../../api/admin";
import OutputPriceModal from "../../components/modals/OutputPriceModal";
import { DataTable } from "../../components/ui/data-table";
import { OutputPrice } from "../../types/output-price";
import { Icons } from "../../assets/images/icons";
import { AppButton } from "../../components/ui/AppButton";
import { ExportButton } from "../../components/ui/ExportButton";
import { useTablePrint } from "../../hooks/useTablePrint";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const getCycle = (priceCycle: number) => {
  let cycle = "JANUARY - MARCH";

  switch (priceCycle) {
    case 1:
      cycle = "JANUARY - MARCH";
      break;
    case 2:
      cycle = "APRIL - JUNE";
      break;
    case 3:
      cycle = "JULY - SEPTEMBER";
      break;
    case 4:
      cycle = "OCTOBER - DECEMBER";
      break;
    default:
      cycle = "JANUARY - MARCH";
      break;
  }
  return cycle;
};

function OutputPricePage() {
  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedId, setSelectedId] = useState<number | undefined>();
  const [open, setOpen] = useState(false);
  const { user } = useAuth() as AuthContextValue;

  /** API */
  const { data: outputPrices, isLoading } = useGetOutputPrice({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });
  /** Table Print */
  const { handlePrint } = useTablePrint();

  /** Transform orders for table */
  const transformedOutputPrices = useMemo((): OutputPrice[] => {
    if (!outputPrices?.data?.content) return [];
    return outputPrices.data.content.map((item: OutputPrice) => ({
      id: item.id,
      product: item.outPut.productName || "N/A",
      warehouseCode: item.warehouse?.centerNo || "N/A",
      year: item.year,
      priceCycle: item.priceCycle,
      contractPrice: item.contractPrice,
      spotPrice: item.spotPrice,
    }));
  }, [outputPrices]);

  /** Columns */
  const outputPriceColumns = [
    {
      key: "product",
      title: "Product",
      dataIndex: "product",
      align: "left" as const,
    },
    {
      key: "warehouseCode",
      title: "Warehouse Code",
      dataIndex: "warehouseCode",
      align: "left" as const,
    },
    {
      key: "year",
      title: "Year",
      dataIndex: "year",
      align: "left" as const,
    },
    {
      key: "priceCycle",
      title: "Price Cycle",
      dataIndex: "priceCycle",
      align: "left" as const,
      render: (priceCycle: number) => {
        const cycle = getCycle(priceCycle);
        return <div>{cycle}</div>;
      },
    },
    {
      key: "contractPrice",
      title: "Contract Price (ETB/kg)",
      dataIndex: "contractPrice",
      align: "right" as const,
    },
    {
      key: "spotPrice",
      title: "Spot Price (ETB/kg)",
      dataIndex: "spotPrice",
      align: "right" as const,
    },
  ];

  const dangerColumns = [
    {
      key: "actions",
      dataIndex: "id",
      title: "Actions",
      render: (_: any, record: any) => {
        return (
          <Button
            variant="default"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedId(record.id);
              setOpen(true);
            }}
          >
            <img
              src={Icons.pencilEdit}
              alt="edit"
              className="h-4 w-4 text-gray-100 mr-1"
            />
            Edit
          </Button>
        );
      },
    },
  ];

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleCreate = () => setOpen(true);

  const hideModal = () => {
    setSelectedId(undefined);
    setOpen(false);
  };

  return (
    <>
      <div className="hidden md:block rounded-xl bg-white">
        <div className="flex flex-col sm:flex-row justify-between">
          <div className="relative w-full lg:w-64">
            <Input
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search here by product name"
              className="pl-10 rounded-2xl w-[308px] h-11"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <img src={Icons.search} alt="search" className="w-6 h-6" />
            </div>
          </div>

          <div className="flex gap-2">
            <AppButton
              customIcon={Icons.printIcon}
              variant="outline"
              onClick={() =>
                handlePrint(
                  outputPriceColumns,
                  transformedOutputPrices,
                  "Output Price Report"
                )
              }
              className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            >
              Print
            </AppButton>

            <ExportButton
              columns={outputPriceColumns}
              data={transformedOutputPrices}
              fileName="output_prices"
              title="Output Price Report"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: outputPrices?.data?.totalElements ?? 0,
              }}
              customIcon={Icons.fileExport}
              className="bg-[#DDDDDD] hover:bg-gray-300"
            />

            {user?.roleName === "ROLE_KFT_SUPER_ADMIN" && (
              <AppButton
                customIcon={Icons.addCircle}
                variant="success"
                onClick={handleCreate}
              >
                Create New
              </AppButton>
            )}
          </div>
        </div>

        {/* Desktop View */}
        <DataTable<OutputPrice>
          data={transformedOutputPrices}
          columns={
            user?.roleName === "ROLE_KFT_SUPER_ADMIN"
              ? [...outputPriceColumns, ...dangerColumns]
              : outputPriceColumns
          }
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: outputPrices?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No output price found"
          rowKey="id"
        />
      </div>

      <OutputPriceModal
        open={open}
        selectedId={selectedId}
        hideModal={hideModal}
      />
    </>
  );
}

export default WithAuth(OutputPricePage);
